import { useState } from "react";
import {
  useDataProvider,
  useGetIdentity,
  useGetList,
  useNotify,
} from "ra-core";
import { Button } from "@/components/ui/button";

import { attachLibraryFile } from "../leads/libraryFiles";
import type { Contact, ContactFile, LibraryFile } from "../types";

const MAX_BYTES = 2_000_000;

export const FileLibrary = () => {
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const { identity } = useGetIdentity();
  const { data: files = [], refetch } = useGetList<LibraryFile>(
    "library_files",
    {
      pagination: { page: 1, perPage: 100 },
      sort: { field: "created_at", order: "DESC" },
    },
  );
  const { data: links = [], refetch: refetchLinks } = useGetList<ContactFile>(
    "contact_files",
    {
      pagination: { page: 1, perPage: 200 },
      sort: { field: "id", order: "ASC" },
    },
  );
  const { data: contacts = [] } = useGetList<Contact>("contacts", {
    pagination: { page: 1, perPage: 100 },
    sort: { field: "last_name", order: "ASC" },
  });
  const [leadByFile, setLeadByFile] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);

  const upload = (file: File | undefined) => {
    if (!file) return;
    if (file.size > MAX_BYTES) {
      notify("Choose a file under 2 MB", { type: "warning" });
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      setBusy(true);
      void dataProvider
        .create("library_files", {
          data: {
            name: file.name,
            mime_type: file.type || "application/octet-stream",
            content: String(reader.result ?? ""),
            sales_id: identity?.id ?? null,
            created_at: new Date().toISOString(),
          },
        })
        .then(() => {
          notify("File saved");
          void refetch();
        })
        .catch(() => notify("Could not save the file", { type: "error" }))
        .finally(() => setBusy(false));
    };
    reader.readAsDataURL(file);
  };

  const attach = async (fileId: LibraryFile["id"]) => {
    const contactId = leadByFile[String(fileId)];
    if (!contactId) return;
    setBusy(true);
    try {
      await attachLibraryFile(dataProvider, contactId, fileId);
      notify("File attached");
      void refetchLinks();
    } catch {
      notify("Could not attach the file", { type: "error" });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mx-auto max-w-3xl">
      <div className="mb-4 flex items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold">Files</h1>
          <p className="text-sm text-muted-foreground">
            Upload a plan, a guide, or a photo once, then attach it to any lead
            or include it on an email.
          </p>
        </div>
        <label className="inline-flex h-9 cursor-pointer items-center rounded-md bg-primary px-3 text-sm font-medium text-primary-foreground">
          Upload
          <input
            type="file"
            className="sr-only"
            aria-label="Upload a file"
            disabled={busy}
            onChange={(event) => {
              upload(event.target.files?.[0]);
              event.target.value = "";
            }}
          />
        </label>
      </div>
      <ul className="space-y-3">
        {files.map((file) => {
          const attached = links.filter((link) => link.file_id === file.id);
          const names = attached
            .map((link) =>
              contacts.find((contact) => contact.id === link.contact_id),
            )
            .filter((contact): contact is Contact => Boolean(contact))
            .map((contact) => `${contact.first_name} ${contact.last_name}`);
          return (
            <li key={file.id} className="rounded-xl border bg-card p-4">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <p className="font-medium">{file.name}</p>
                  <p className="text-sm text-muted-foreground">
                    {names.length
                      ? `On ${names.join(", ")}`
                      : "Not attached yet"}
                  </p>
                </div>
                <a
                  className="text-sm text-sky-700 underline"
                  href={file.content}
                  download={file.name}
                >
                  Download
                </a>
              </div>
              {file.mime_type?.startsWith("image/") ? (
                <img
                  src={file.content}
                  alt=""
                  className="mt-3 h-16 rounded border object-contain"
                />
              ) : null}
              <div className="mt-3 flex flex-wrap items-center gap-2">
                <select
                  aria-label={`Attach ${file.name}`}
                  className="h-8 rounded-md border bg-transparent px-2 text-sm"
                  value={leadByFile[String(file.id)] ?? ""}
                  onChange={(event) =>
                    setLeadByFile((current) => ({
                      ...current,
                      [String(file.id)]: event.target.value,
                    }))
                  }
                >
                  <option value="">Choose a lead</option>
                  {contacts.map((contact) => (
                    <option key={contact.id} value={String(contact.id)}>
                      {contact.first_name} {contact.last_name}
                    </option>
                  ))}
                </select>
                <Button
                  type="button"
                  size="sm"
                  variant="secondary"
                  disabled={busy || !leadByFile[String(file.id)]}
                  onClick={() => void attach(file.id)}
                >
                  Attach
                </Button>
              </div>
            </li>
          );
        })}
      </ul>
      {files.length === 0 ? (
        <p className="text-sm text-muted-foreground">No files yet.</p>
      ) : null}
    </div>
  );
};

FileLibrary.path = "/files";
